/**
 * _publicBlocks.jsx : blocs visuels purs de la page du lien de séance (D52 : privé au dossier).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, RADIUS, TYPE } from '../../constants/theme';
import { semanticColors } from '../../constants/semanticColors';
import { formatHumanDuration } from '../../utils/formatDuration';
import { formatDateLocale, formatSessionTitle, formatTime } from '../../utils/formatters';
import LanguageSwitcher from '../../shared/LanguageSwitcher';
import { formatLinkDate, periodRangeText, publicErrorText } from './linkText';

/** Bandeau de la page : marque et sélecteur de langue, sans menu de l'application. */
export function PublicTopBar() {
  return (
    <header style={{
      background: BRAND.gradientHeader, color: '#fff', padding: '12px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <span style={{ ...TYPE.h3, color: '#fff' }}>EkynAir</span>
      <LanguageSwitcher />
    </header>
  );
}

/** En-tête de la séance partagée : cheval, titre, dates, portée, validité du lien. */
export function PublicLinkHeader({ session, analysis, expiresAt, theme, actions }) {
  const { t } = useTranslation();
  const line = { ...TYPE.body, margin: '6px 0 0', color: theme.textSecondary };
  return (
    <section data-testid="public-link-header" style={{ marginBottom: 20, display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'space-between' }}>
      <div style={{ flex: 1, minWidth: 260 }}>
        <span style={{
          ...TYPE.body, fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: RADIUS.pill,
          background: semanticColors.blueBg, color: BRAND.blueText,
        }}>
          {t('sessionLink.public.badge')}
        </span>
        {session.horseName && (
          <div style={{ ...TYPE.h3, fontSize: 13, fontWeight: 700, marginTop: 12, color: BRAND.blueText,
            textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {session.horseName}
          </div>
        )}
        <h1 style={{ ...TYPE.h1, fontSize: 26, margin: '4px 0 0', color: theme.textPrimary }}>{formatSessionTitle(session.title)}</h1>
        <p style={line}>
          {formatDateLocale(session.start_time, { year: 'numeric', month: 'long', day: 'numeric' })}
          {' • '}
          {t('session.header.timeRange', { start: formatTime(session.start_time), end: formatTime(session.end_time) })}
          {' • '}
          {formatHumanDuration(new Date(session.end_time) - new Date(session.start_time))}
        </p>
        <p data-testid="public-link-scope" style={line}>
          {analysis.active
            ? t('sessionLink.scope.period', { range: periodRangeText(analysis.window.start, analysis.window.end) })
            : t('sessionLink.scope.whole')}
        </p>
        <p data-testid="public-link-valid-until" style={{ ...line, fontWeight: 600 }}>
          {t('sessionLink.public.validUntil', { date: formatLinkDate(expiresAt) })}
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>{actions}</div>
    </section>
  );
}

/** Erreur du lien : message dédié, aucune donnée, invitation à redemander un lien. */
export function PublicLinkError({ code, theme }) {
  const { t } = useTranslation();
  return (
    <section role="alert" data-testid={`public-link-error-${code}`} style={{
      maxWidth: 560, margin: '40px auto', padding: 24, borderRadius: RADIUS.lg,
      background: theme.card, border: `1px solid ${theme.border}`, textAlign: 'center',
    }}>
      <h1 style={{ ...TYPE.h2, margin: '0 0 12px', color: theme.textPrimary }}>{t('sessionLink.public.error.title')}</h1>
      <p style={{ ...TYPE.body, margin: 0, color: theme.textPrimary }}>{publicErrorText(code, t)}</p>
      <p style={{ ...TYPE.body, margin: '12px 0 0', color: theme.textSecondary }}>{t('sessionLink.public.error.askNew')}</p>
    </section>
  );
}
